import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from "@angular/core";
import { Emoji, EmojiApi } from "@app/shared/sdk";
import { environment } from "@env/environment";
import { GameSettingsService } from "../game-settings.service";
import { MatDialogRef, MatDialog } from "@angular/material";
import { Subject } from "rxjs";
import { filter, takeUntil, tap } from "rxjs/operators";
import { ConfirmationDialogComponent } from "../../confirmation-dialog/confirmation-dialog.component";
import { FormGroup } from "@angular/forms";
import { SortablejsOptions } from "angular-sortablejs";
import * as _ from "lodash";

interface Screen {
  title: string;
  groupTitle?: string;
  emojis: Emoji[];
  leftEmojis?: Emoji[];
  rightEmojis?: Emoji[];
  dressUpSlots?: (Emoji | null)[];
}

interface Tab {
  title: string;
  screens: Screen[];
}

@Component({
  selector: "all-screens-container",
  templateUrl: "./all-screens-container.component.html",
  styleUrls: ["./all-screens-container.component.scss"],
})
export class AllScreensContainerComponent implements OnChanges, OnDestroy {
  @Input("emojis") emojis: Emoji[] = [];
  @Input("dressUpGroups") dressUpGroups: any[] = [];
  @Input("allGlobalEmojis") allGlobalEmojis: any[] = [];
  @Input("dressUpFigure") dressUpFigure: string | null = null;
  @Input("dressUpFigureUrl") dressUpFigureUrl: string | null = null;
  @Input("gridType") gridType: any = "";
  @Input("gameFormat") gameFormat: string = "standard";
  @Input("isRounded") isRounded: boolean = false;
  @Input("editedGameId") editedGameId = "";
  @Input("verticalStepperStep2") verticalStepperStep2: FormGroup;
  tabs: Tab[] = [];
  removedEmojis: any[] = [];
  dressUpScreensInitialized: boolean = false;

  seatRows = [1, 2, 3, 4, 5, 6, 7, 8];
  leftSeatCols = ["A", "B", "C"];
  rightSeatCols = ["D", "E", "F"];

  s3BucketUrl: string = environment.s3BucketUrl;
  fallbackImage = "/assets/images/image-not-found-150x150.png";
  fileNameDialogRef: MatDialogRef<ConfirmationDialogComponent>;

  private _unsubscribeAll: Subject<any>;

  selectedTabIndex = 0; // Default selected tab index

  emojisAddedArray = this.gameSettingsService.emojiAdded$;

  constructor(
    public gameSettingsService: GameSettingsService,
    private dialog: MatDialog,
    private emojiApi: EmojiApi
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
    this.onEmojiAddedAddItToScreens();
    this.loadGlobalEmojis();

    this.gameSettingsService.emojisPopulated$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.dressUpScreensInitialized = false;
        this.removedEmojis = [];
        this.gameSettingsService.updateRemovedEmojisArray([]);
        if (this.verticalStepperStep2) {
          this.verticalStepperStep2.setErrors(null);
        }
        this.getScreens();
      });
  }

  loadGlobalEmojis() {
    this.emojiApi
      .find({ where: { isActive: true }, order: "order ASC", limit: 1000 })
      .subscribe((emojis: any) => {
        this.allGlobalEmojis = Array.isArray(emojis) ? emojis : Object.values(emojis || {});
        if (this.gameFormat === "dressUp" && !this.dressUpScreensInitialized) {
          this.getScreens();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.gameFormat) {
      this.dressUpScreensInitialized = false;
      this.getScreens();
      return;
    }

    if (this.gameFormat === "dressUp") {
      if (changes.dressUpGroups) {
        this.dressUpScreensInitialized = false;
        this.getScreens();
        return;
      }

      if (!this.dressUpScreensInitialized) {
        if (changes.emojis) {
          this.getScreens();
        }
        return;
      }

      if (
        changes.emojis &&
        changes.emojis.currentValue &&
        this.isMateriallyDifferentEmojis(changes.emojis.currentValue)
      ) {
        this.dressUpScreensInitialized = false;
        this.removedEmojis = [];
        this.gameSettingsService.updateRemovedEmojisArray([]);
        if (this.verticalStepperStep2) {
          this.verticalStepperStep2.setErrors(null);
        }
        this.getScreens();
      }
      return;
    }

    if (
      changes.emojis ||
      changes.gridType ||
      changes.gameFormat
    ) {
      this.getScreens();
    }
  }

  private isMateriallyDifferentEmojis(newEmojis: Emoji[]): boolean {
    if (!Array.isArray(newEmojis) || newEmojis.length === 0) return false;
    const currentEmojis = this.getEmojisFromTabs(this.tabs);
    if (currentEmojis.length === 0) return true;
    const currentIdSet = new Set(
      currentEmojis.map((e) =>
        String(e.id || (e as any)._id || ((e as any).emoji && ((e as any).emoji.id || (e as any).emoji._id)))
      )
    );
    let differentCount = 0;
    for (const e of newEmojis) {
      const id = String(e.id || (e as any)._id || ((e as any).emoji && ((e as any).emoji.id || (e as any).emoji._id)));
      if (!currentIdSet.has(id)) {
        differentCount++;
      }
    }
    return differentCount > 5;
  }

  resolveEmojiObjects(emojiIds: string[]): Emoji[] {
    if (!Array.isArray(emojiIds)) return [];
    const emojiMap: { [id: string]: Emoji } = {};

    const registerEmoji = (e: any) => {
      if (!e) return;
      if (e.id) emojiMap[String(e.id)] = e;
      if (e._id) emojiMap[String(e._id)] = e;
      if (e.emoji) {
        if (e.emoji.id) emojiMap[String(e.emoji.id)] = e.emoji;
        if (e.emoji._id) emojiMap[String(e.emoji._id)] = e.emoji;
      }
    };

    if (Array.isArray(this.allGlobalEmojis)) {
      this.allGlobalEmojis.forEach(registerEmoji);
    }
    if (Array.isArray(this.emojis)) {
      this.emojis.forEach(registerEmoji);
    }
    if (this.gameSettingsService) {
      if (Array.isArray(this.gameSettingsService.extraEmojisSet)) {
        this.gameSettingsService.extraEmojisSet.forEach(registerEmoji);
      }
      if (Array.isArray(this.gameSettingsService.defaultEmojis)) {
        this.gameSettingsService.defaultEmojis.forEach(registerEmoji);
      }
      if (Array.isArray(this.gameSettingsService.emojis)) {
        this.gameSettingsService.emojis.forEach(registerEmoji);
      }
    }

    return emojiIds
      .map((id) => emojiMap[String(id)])
      .filter((e) => !!e);
  }

  getDressUpSlots(screen: Screen): (Emoji | null)[] {
    if (screen) {
      if (!screen.dressUpSlots || screen.dressUpSlots.length !== 8) {
        const slots: (Emoji | null)[] = [];
        const emojis = Array.isArray(screen.emojis) ? screen.emojis : [];
        for (let i = 0; i < 8; i++) {
          slots.push(emojis[i] || null);
        }
        screen.dressUpSlots = slots;
      }
      return screen.dressUpSlots;
    }
    return [];
  }

  resolvedFigureSrc(): string {
    if (this.dressUpFigureUrl) return this.dressUpFigureUrl;
    if (this.dressUpFigure) return this.s3BucketUrl + this.dressUpFigure;
    return this.fallbackImage;
  }

  onFigureImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.getAttribute("src") === this.fallbackImage) return;
    img.src = this.fallbackImage;
  }

  onImgError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.getAttribute("src") === this.fallbackImage) return;
    img.src = this.fallbackImage;
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onEmojiAddedAddItToScreens() {
    this.emojisAddedArray
      .pipe(
        takeUntil(this._unsubscribeAll),
        filter((emoji) => emoji !== null),
        tap((emoji) => {
          const removedEmojiData = this.removedEmojis.shift();
          const { tabIndex, screenIndex, emojiIndex } = removedEmojiData;
          this.tabs[tabIndex].screens[screenIndex].emojis.splice(
            emojiIndex,
            0,
            emoji
          );
          if (this.gameFormat === "seatMap") {
            const sc = this.tabs[tabIndex].screens[screenIndex];
            if (sc) {
              sc.leftEmojis = sc.emojis.slice(0, 24);
              sc.rightEmojis = sc.emojis.slice(24, 48);
            }
          }
          if (this.gameFormat === "dressUp") {
            const sc = this.tabs[tabIndex].screens[screenIndex];
            if (sc) {
              const slots: (Emoji | null)[] = [];
              for (let i = 0; i < 8; i++) {
                slots.push(sc.emojis[i] || null);
              }
              sc.dressUpSlots = slots;
            }
          }
          this.gameSettingsService.updateEmojiAdded(null);
          this.gameSettingsService.updateRemovedEmojisArray(this.removedEmojis);
        })
      )
      .subscribe(() => {
        const emojisLengths = this.tabs.map((tab) => {
          const screensLengths = tab.screens.map(
            (screen) => screen.emojis.length
          );
          return screensLengths.reduce((sum, length) => sum + length, 0);
        });

        const totalEmojis = emojisLengths.reduce((sum, len) => sum + len, 0);
        if (totalEmojis === 48 || this.removedEmojis.length === 0) {
          this.verticalStepperStep2.setErrors(null);
          const allEmojis = this.tabs.reduce((result, tab) => {
            const emojis = tab.screens.reduce(
              (screenEmojis, screen) => screenEmojis.concat(screen.emojis),
              []
            );
            return result.concat(emojis);
          }, []);
          if (this.editedGameId !== undefined) {
            this.gameSettingsService.extraEmojisSet =
              this.gameSettingsService.extraEmojisSet.map((el, index) => ({
                ...el,
                emoji: allEmojis[index],
                emojiId: allEmojis[index].id,
              }));
          } else {
            this.gameSettingsService.extraEmojisSet = allEmojis;
          }
        }
      });
  }

  getScreens(): Tab[] {
    this.tabs = []; // Initialize an empty array to store tabs

    const sourceEmojis =
      (this.gameSettingsService && Array.isArray(this.gameSettingsService.extraEmojisSet) && this.gameSettingsService.extraEmojisSet.length > 0)
        ? this.gameSettingsService.extraEmojisSet
        : this.emojis;

    const emojis = [
      ...this.gameSettingsService.reorganizeEmojisAndConcatenate(
        this.editedGameId,
        sourceEmojis
      ),
    ];

    if (this.gameFormat === "seatMap") {
      let screens: Screen[] = [];
      const emojisPerScreen = 48;
      for (let i = 0; i < Math.max(emojis.length, emojisPerScreen); i += emojisPerScreen) {
        const screenEmojis = emojis.slice(i, i + emojisPerScreen);
        screens.push({
          title: `Screen-${Math.floor(i / emojisPerScreen) + 1}`,
          emojis: screenEmojis,
          leftEmojis: screenEmojis.slice(0, 24),
          rightEmojis: screenEmojis.slice(24, 48),
        });
      }
      this.tabs = [
        {
          title: "Screens",
          screens: screens,
        },
      ];
      this.gameSettingsService.currentTabs = this.tabs;
      return this.tabs;
    }

    if (this.gameFormat === "dressUp") {
      let screens: Screen[] = [];
      const bodyPartNames = ["hats", "glasses", "shirts", "pants", "shoes", "necklaces"];
      const groupLabels = ["Hats", "Glasses", "Shirts", "Pants", "Shoes", "Necklaces"];
      for (let i = 0; i < 6; i++) {
        let groupEmojis = emojis.slice(i * 8, i * 8 + 8);
        if (groupEmojis.length === 0 && Array.isArray(this.dressUpGroups)) {
          const group = this.dressUpGroups.find((g) => g.bodyPart === bodyPartNames[i]);
          if (group && Array.isArray(group.emojiIds) && group.emojiIds.length > 0) {
            groupEmojis = this.resolveEmojiObjects(group.emojiIds);
          }
        }
        const dressUpSlots: (Emoji | null)[] = [];
        for (let j = 0; j < 8; j++) {
          dressUpSlots.push(groupEmojis[j] || null);
        }
        screens.push({
          title: `Screen-${i + 1}`,
          groupTitle: groupLabels[i],
          emojis: groupEmojis,
          dressUpSlots: dressUpSlots,
        });
      }
      this.tabs = [
        { title: "Screen 1-3", screens: screens.slice(0, 3) },
        { title: "Screen 4-6", screens: screens.slice(3, 6) },
      ];
      this.dressUpScreensInitialized = true;
      this.gameSettingsService.currentTabs = this.tabs;
      return this.tabs;
    }

    let emojisPerScreen =
      this.gridType === "4"
        ? 16
        : this.gridType === "2"
          ? 6
          : 48;

    let screens = [];

    for (let i = 0; i < emojis.length; i += emojisPerScreen) {
      screens.push({
        title: `Screen-${i / emojisPerScreen + 1}`,
        emojis: emojis.slice(i, i + emojisPerScreen),
      });
    }

    if (this.gridType === "2") {
      for (let j = 0; j < 3; j++) {
        this.tabs[j] = {
          title: `Screen ${j * 3 + 1}-${j * 3 + (j === 2 ? 2 : 3)}`,
          screens: screens.slice(j * 3, j * 3 + 3),
        };
      }
    } else {
      this.tabs[0] = { title: `Screens`, screens: screens };
    }
    this.gameSettingsService.currentTabs = this.tabs;
    return this.tabs;
  }

  /**
   *
   * @param data
   * @param tabIndex
   * @param screenIndex
   */
  removeEmojiFromScreen(data: {
    emojiIndex: number;
    emoji: Emoji;
    tabIndex: number;
    screenIndex: number;
  }) {
    this.fileNameDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: "Delete Emoji",
        message: "Are you sure you want to delete this emoji from screen?",
      },
    });
    this.fileNameDialogRef
      .afterClosed()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (result) => {
        if (result) {
          const targetTab = this.tabs[data.tabIndex] || this.tabs[0];
          targetTab.screens[data.screenIndex].emojis = targetTab.screens[
            data.screenIndex
          ].emojis.filter((el) => el.id !== data.emoji.id);
          this.verticalStepperStep2.setErrors({ screensNotFull: true });

          // Keep seatMap leftEmojis and rightEmojis in sync with the deletion
          if (this.gameFormat === "seatMap") {
            const sc = targetTab.screens[data.screenIndex];
            if (sc) {
              if (sc.leftEmojis) {
                sc.leftEmojis = sc.leftEmojis.filter((el) => el && el.id !== data.emoji.id);
              }
              if (sc.rightEmojis) {
                sc.rightEmojis = sc.rightEmojis.filter((el) => el && el.id !== data.emoji.id);
              }
            }
          }

          // Keep dressUpGroups & dressUpSlots in sync with the deletion
          if (this.gameFormat === "dressUp") {
            const sc = targetTab.screens[data.screenIndex];
            if (sc) {
              const slots: (Emoji | null)[] = [];
              for (let i = 0; i < 8; i++) {
                slots.push(sc.emojis[i] || null);
              }
              sc.dressUpSlots = slots;
            }
            if (Array.isArray(this.dressUpGroups)) {
              const emojiId = String(data.emoji.id || (data.emoji as any)._id);
              for (const group of this.dressUpGroups) {
                if (Array.isArray(group.emojiIds)) {
                  group.emojiIds = group.emojiIds.filter((id: string) => String(id) !== emojiId);
                }
              }
            }
          }

          this.removedEmojis.push(data);
          this.gameSettingsService.updateRemovedEmojisArray(this.removedEmojis);
        }
      });
  }

  isCompressedCheck = (screen: Screen) => {
    return (
      !!screen &&
      Array.isArray(screen.emojis) &&
      screen.emojis.length > 0 &&
      screen.emojis.every((el) => el && el.isCompressed)
    );
  };

  getDynamicOptions(index: number): SortablejsOptions {
    return {
      group: {
        name: `group${index}`,
      },
      sort: this.gameFormat !== "dressUp",
      disabled: this.gameFormat === "dressUp",
      onEnd: (event: any) => {
        if (this.gameFormat === "dressUp") return;
        this.updateEmojisOrder(index, event);
      },
      // Other sortablejs options if needed
    };
  }

  getDressUpDynamicOptions(
    tabIndex: number,
    screenIndex: number
  ): SortablejsOptions {
    return {
      group: {
        name: `dressUpScreen_${tabIndex}_${screenIndex}`,
      },
      sort: true,
      disabled: false,
      filter: ".dressUpPlaceholderSlot",
      onEnd: (event: any) => {
        this.onDressUpSortEnd(tabIndex, screenIndex, event);
      },
    };
  }

  onDressUpSortEnd(tabIndex: number, screenIndex: number, event: any) {
    const targetTab = this.tabs[tabIndex] || this.tabs[0];
    if (!targetTab || !targetTab.screens || !targetTab.screens[screenIndex]) return;

    const screen = targetTab.screens[screenIndex];
    if (!screen.dressUpSlots) return;

    // Filter non-null emojis from the reordered dressUpSlots
    screen.emojis = screen.dressUpSlots.filter(
      (e): e is Emoji => e !== null && e !== undefined
    );

    // Keep placeholder slots at the end of dressUpSlots
    const slots: (Emoji | null)[] = [];
    for (let i = 0; i < 8; i++) {
      slots.push(screen.emojis[i] || null);
    }
    screen.dressUpSlots = slots;

    // Sync into dressUpGroups for this screen/group
    const groupIndex = tabIndex * 3 + screenIndex;
    if (Array.isArray(this.dressUpGroups) && this.dressUpGroups[groupIndex]) {
      this.dressUpGroups[groupIndex].emojiIds = screen.emojis
        .map((e: any) => (e && (e.id || e._id || (e.emoji && (e.emoji.id || e.emoji._id)))) || String(e))
        .filter((id: any) => !!id && id !== "undefined");
    }

    // Update global extraEmojisSet so that syncDressUpGroupsFromScreens() at save time has the new order
    this.updateEmojisOrder(groupIndex, event);
  }

  getSeatMapDynamicOptions(
    tabIndex: number,
    screenIndex: number,
    aisle: "left" | "right"
  ): SortablejsOptions {
    return {
      group: {
        name: `seatMapGroup_${tabIndex}_${screenIndex}`,
      },
      sort: true,
      disabled: false,
      onEnd: (event: any) => {
        this.onSeatMapSortEnd(tabIndex, screenIndex, event);
      },
    };
  }

  onSeatMapSortEnd(tabIndex: number, screenIndex: number, event: any) {
    const targetTab = this.tabs[tabIndex] || this.tabs[0];
    if (!targetTab || !targetTab.screens || !targetTab.screens[screenIndex]) return;

    const screen = targetTab.screens[screenIndex];
    screen.emojis = [
      ...(screen.leftEmojis || []),
      ...(screen.rightEmojis || []),
    ];

    this.updateEmojisOrder(screenIndex, event);
  }

  updateEmojisOrder(screenIndex, event) {
    const allEmojis = this.getEmojisFromTabs(this.tabs);

    this.gameSettingsService.extraEmojisSet =
      this.gameSettingsService.extraEmojisSet.map((el, index) => ({
        ...el,
        emoji: allEmojis[index],
        emojiId: allEmojis[index] ? (allEmojis[index].id || (allEmojis[index] as any)._id) : (el ? el.emojiId : null),
      }));

    this.gameSettingsService.shufflePrizes(allEmojis);
  }

  getEmojisFromTabs(tabs: Tab[]): Emoji[] {
    let emojis: Emoji[] = [];

    // Iterate through each tab
    for (const tab of tabs) {
      // Iterate through each screen within the tab
      for (const screen of tab.screens) {
        // Concatenate the emojis from each screen to the emojis array
        emojis = emojis.concat(screen.emojis);
      }
    }

    return emojis;
  }

  trackByFn(index: number, item: any) {
    return index; // or return unique identifier if available, like item.id
  }

  // Method to handle tab selection change
  tabChanged(event: any) {
    this.selectedTabIndex = event.index;
  }

  trackByIndex(index: number) {
    return index;
  }
}
